/**
 * LaneBooker admin — Documentation portal.
 *
 * Lazy-loads a Markdown index + topic files from /docs/ and renders them
 * into the #docs-dashboard panel. Includes a sidebar with title+intro
 * search and deep-linkable hash routes (#docs/<slug>).
 *
 * No external dependencies — uses a small in-house Markdown subset
 * renderer (h1-h3, paragraphs, lists, code, bold/italic, links,
 * blockquotes, tables) to keep bundle impact ~0 KB.
 */

type DocTopic = {
  slug: string;
  title: string;
  intro: string;
  plan?: 'Pro' | 'Scale';
};

type DocIndex = { topics: DocTopic[] };

const DOCS_BASE = '/docs';

let docIndex: DocIndex | null = null;
let docsBooted = false;
const mdCache = new Map<string, string>();

/* ----------------------------- Markdown ----------------------------- */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderInline(src: string): string {
  let s = escapeHtml(src);
  // inline code
  s = s.replace(/`([^`]+)`/g, (_m, c) => `<code>${c}</code>`);
  // bold then italic
  s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  s = s.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  // links [text](href)
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, href) => {
    const safeHref = /^(https?:|mailto:|#|\/)/.test(href) ? href : '#';
    const external = /^https?:/.test(safeHref);
    const attrs = external ? ' target="_blank" rel="noopener noreferrer"' : '';
    return `<a href="${safeHref}"${attrs}>${text}</a>`;
  });
  return s;
}

function renderMarkdown(src: string): string {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;

  const flushList = (buf: string[], ordered: boolean) => {
    if (buf.length === 0) return;
    const tag = ordered ? 'ol' : 'ul';
    out.push(`<${tag}>`);
    for (const item of buf) out.push(`<li>${renderInline(item)}</li>`);
    out.push(`</${tag}>`);
    buf.length = 0;
  };

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    if (/^```/.test(line)) {
      const lang = line.slice(3).trim();
      i++;
      const code: string[] = [];
      while (i < lines.length && !/^```/.test(lines[i])) {
        code.push(lines[i]);
        i++;
      }
      i++; // closing ```
      const langClass = lang ? ` class="lang-${escapeHtml(lang)}"` : '';
      out.push(`<pre><code${langClass}>${escapeHtml(code.join('\n'))}</code></pre>`);
      continue;
    }

    // Blank line
    if (/^\s*$/.test(line)) {
      i++;
      continue;
    }

    // Headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = Math.min(h[1].length, 6);
      out.push(`<h${level}>${renderInline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // Blockquote (collect consecutive > lines, detect plan callout)
    if (/^>\s?/.test(line)) {
      const block: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        block.push(lines[i].replace(/^>\s?/, ''));
        i++;
      }
      const joined = block.join(' ').trim();
      const planMatch = /^\*\*(Scale plan only|Pro plan or higher|Pro plan only)\.\*\*\s*(.*)$/i.exec(joined);
      if (planMatch) {
        const label = /scale/i.test(planMatch[1]) ? 'Scale' : 'Pro';
        const rest = planMatch[2];
        out.push(
          `<div class="docs-callout docs-callout-${label.toLowerCase()}">` +
            `<span class="docs-pill docs-pill-${label.toLowerCase()}">${label}</span>` +
            `<span class="docs-callout-text">${renderInline(rest)}</span>` +
            `</div>`,
        );
      } else {
        out.push(`<blockquote>${renderInline(joined)}</blockquote>`);
      }
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        buf.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      flushList(buf, false);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const buf: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        buf.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      flushList(buf, true);
      continue;
    }

    // Table (very small subset: header | --- | rows)
    if (/\|/.test(line) && i + 1 < lines.length && /^\s*\|?\s*-{2,}/.test(lines[i + 1])) {
      const headerCells = line
        .replace(/^\||\|$/g, '')
        .split('|')
        .map((c) => c.trim());
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /\|/.test(lines[i]) && !/^\s*$/.test(lines[i])) {
        rows.push(
          lines[i]
            .replace(/^\||\|$/g, '')
            .split('|')
            .map((c) => c.trim()),
        );
        i++;
      }
      out.push('<table class="docs-table"><thead><tr>');
      for (const h2 of headerCells) out.push(`<th>${renderInline(h2)}</th>`);
      out.push('</tr></thead><tbody>');
      for (const row of rows) {
        out.push('<tr>');
        for (const c of row) out.push(`<td>${renderInline(c)}</td>`);
        out.push('</tr>');
      }
      out.push('</tbody></table>');
      continue;
    }

    // Paragraph (collect until blank line)
    const para: string[] = [line];
    i++;
    while (
      i < lines.length &&
      !/^\s*$/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^[-*]\s+/.test(lines[i]) &&
      !/^\d+\.\s+/.test(lines[i]) &&
      !/^>\s?/.test(lines[i]) &&
      !/^```/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    out.push(`<p>${renderInline(para.join(' '))}</p>`);
  }

  return out.join('\n');
}

/* ------------------------------ Loader ----------------------------- */

async function loadIndex(): Promise<DocIndex> {
  if (docIndex) return docIndex;
  const res = await fetch(`${DOCS_BASE}/index.json`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`docs index ${res.status}`);
  docIndex = (await res.json()) as DocIndex;
  return docIndex;
}

async function loadTopic(slug: string): Promise<string> {
  const cached = mdCache.get(slug);
  if (cached) return cached;
  const res = await fetch(`${DOCS_BASE}/${encodeURIComponent(slug)}.md`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`doc ${slug} ${res.status}`);
  const text = await res.text();
  mdCache.set(slug, text);
  return text;
}

/* ------------------------------- Render ---------------------------- */

function renderSidebar(filter: string) {
  const list = document.getElementById('docs-list');
  if (!list || !docIndex) return;
  const q = filter.trim().toLowerCase();
  const matches = docIndex.topics.filter((t) => {
    if (!q) return true;
    return t.title.toLowerCase().includes(q) || t.intro.toLowerCase().includes(q);
  });
  if (matches.length === 0) {
    list.innerHTML = '<div class="docs-empty">No topics match.</div>';
    return;
  }
  list.innerHTML = matches
    .map((t) => {
      const pill = t.plan
        ? ` <span class="docs-pill docs-pill-${t.plan.toLowerCase()}">${t.plan}</span>`
        : '';
      return (
        `<button class="docs-topic" data-slug="${t.slug}">` +
        `<span class="docs-topic-title">${escapeHtml(t.title)}${pill}</span>` +
        `<span class="docs-topic-intro">${escapeHtml(t.intro)}</span>` +
        `</button>`
      );
    })
    .join('');
  list.querySelectorAll<HTMLButtonElement>('.docs-topic').forEach((btn) => {
    btn.addEventListener('click', () => {
      const slug = btn.dataset.slug!;
      window.location.hash = `#docs/${slug}`;
    });
  });
  highlightActive();
}

function highlightActive() {
  const slug = currentSlug();
  document.querySelectorAll<HTMLButtonElement>('#docs-list .docs-topic').forEach((b) => {
    b.classList.toggle('active', b.dataset.slug === slug);
  });
}

function currentSlug(): string {
  const m = /^#docs\/([a-z0-9-]+)/.exec(window.location.hash);
  return m ? m[1] : '';
}

async function renderTopic(slug: string) {
  const main = document.getElementById('docs-main');
  if (!main) return;
  if (!docIndex) await loadIndex();
  const topic = docIndex!.topics.find((t) => t.slug === slug);
  if (!topic) {
    main.innerHTML =
      '<div class="docs-placeholder"><h2>Documentation</h2>' +
      '<p>Pick a topic from the left to get started.</p></div>';
    return;
  }
  main.innerHTML = '<div class="docs-loading">Loading…</div>';
  try {
    const md = await loadTopic(slug);
    const html = renderMarkdown(md);
    const planPill = topic.plan
      ? `<span class="docs-pill docs-pill-${topic.plan.toLowerCase()}">${topic.plan}</span>`
      : '';
    main.innerHTML =
      `<article class="docs-article">` +
      (planPill ? `<div class="docs-article-meta">${planPill}</div>` : '') +
      html +
      `</article>`;
    // Intercept in-doc anchors that point to other topics
    main.querySelectorAll<HTMLAnchorElement>('a[href^="#docs/"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.hash = a.getAttribute('href')!;
      });
    });
    main.scrollTop = 0;
  } catch (err) {
    main.innerHTML =
      `<div class="docs-error"><h2>Couldn't load this topic</h2>` +
      `<p>${escapeHtml(String(err))}</p></div>`;
  }
}

/* ------------------------------ Public ----------------------------- */

/**
 * Mount the Docs panel. Called the first time the user clicks the
 * "Docs" tab. Idempotent — subsequent calls just refresh routing.
 */
export async function mountDocs(): Promise<void> {
  const panel = document.getElementById('docs-dashboard');
  if (!panel) return;
  if (!docsBooted) {
    docsBooted = true;
    panel.innerHTML = `
      <div class="reports-header">
        <h2>Documentation</h2>
        <p class="reports-subtitle">Guides for setting up and running LaneBooker. Search or browse below.</p>
      </div>
      <div class="docs-layout">
        <aside class="docs-sidebar">
          <input type="search" id="docs-search" class="docs-search" placeholder="Search docs…" autocomplete="off" />
          <div id="docs-list" class="docs-list"></div>
        </aside>
        <section id="docs-main" class="docs-main"></section>
      </div>
    `;

    try {
      await loadIndex();
    } catch (err) {
      panel.innerHTML =
        `<div class="docs-error"><h2>Documentation unavailable</h2>` +
        `<p>${escapeHtml(String(err))}</p></div>`;
      return;
    }

    renderSidebar('');

    const search = document.getElementById('docs-search') as HTMLInputElement | null;
    if (search) {
      search.addEventListener('input', () => renderSidebar(search.value));
    }

    window.addEventListener('hashchange', () => {
      const slug = currentSlug();
      if (slug) {
        void renderTopic(slug);
        highlightActive();
      }
    });
  }

  // Route to current slug (if any) or fall back to first topic.
  const slug = currentSlug();
  if (slug) {
    void renderTopic(slug);
  } else if (docIndex && docIndex.topics.length > 0) {
    void renderTopic(docIndex.topics[0].slug);
    // Don't push hash on initial mount — keeps history clean.
  }
  highlightActive();
}
